import { normalizeDomainTarget } from "./ruleDerivation";
import type { TargetConfig } from "./ruleTypes";

export type WebsiteCategoryId = "social" | "meta" | "messaging" | "news" | "dating";

export type WebsiteCategory = {
  id: WebsiteCategoryId;
  domains: readonly string[];
};

export const WEBSITE_CATEGORY_IDS: WebsiteCategoryId[] = [
  "social",
  "meta",
  "messaging",
  "news",
  "dating",
];

const SOCIAL_DOMAINS = [
  "9gag.com",
  "classmates.com",
  "facebook.com",
  "instagram.com",
  "meetup.com",
  "parse.com",
  "pinterest.com",
  "reddit.com",
  "snapchat.com",
  "swarmapp.com",
  "t.co",
  "telesco.pe",
  "threads.net",
  "tumblr.com",
  "vine.co",
  "vk.com",
  "x.com",
] as const;

const META_DOMAINS = ["facebook.com", "instagram.com", "whatsapp.com", "messenger.com"] as const;

const MESSAGING_DOMAINS = [
  "discord.com",
  "instagram.com",
  "line.me",
  "messenger.com",
  "skype.com",
  "slack.com",
  "snapchat.com",
  "t.me",
  "telegram.org",
  "viber.com",
  "wechat.com",
  "whatsapp.com",
] as const;

const NEWS_DOMAINS = [
  "abcnews.go.com",
  "arstechnica.com",
  "bbc.com",
  "theguardian.com",
  "huffingtonpost.com",
  "huffpost.com",
  "independent.co.uk",
  "latimes.com",
  "macrumors.com",
  "msn.com",
  "msnbc.com",
  "nbcnews.com",
  "news.google.com",
  "newyorker.com",
  "npr.org",
  "nydailynews.com",
  "nymag.com",
  "nypost.com",
  "nytimes.com",
  "people.com",
  "politico.com",
  "reuters.com",
  "salon.com",
  "slate.com",
  "spiegel.de",
  "bloomberg.com",
  "breitbart.com",
  "businessinsider.com",
  "businessinsider.com.pl",
  "businessinsider.de",
  "businessinsider.es",
  "businessinsider.in",
  "businessinsider.jp",
  "businessinsider.nl",
  "buzzfeed.com",
  "cbsnews.com",
  "cnbc.com",
  "cnn.com",
  "dailymail.co.uk",
  "feedly.com",
  "forbes.com",
  "foxnews.com",
  "telegraph.co.uk",
  "theatlantic.com",
  "theverge.com",
  "usatoday.com",
  "vice.com",
  "washingtonpost.com",
  "wired.com",
  "wsj.com",
] as const;

const DATING_DOMAINS = [
  "badoo.com",
  "blackpeoplemeet.com",
  "bumbcdn.com",
  "seniorpeoplemeet.com",
  "speeddate.com",
  "theinnercircle.co",
  "tinder.com",
  "zoosk.com",
  "bumble.com",
  "chemistry.com",
  "christianmingle.com",
  "coffeemeetsbagel.com",
  "datehookup.com",
  "eharmony.com",
  "elitesingles.com",
  "girlfriendsmeet.com",
  "grindr.com",
  "her.com",
  "hinge.co",
  "howaboutwe.com",
  "jdate.com",
  "match.com",
  "okcupid.com",
  "ourtime.com",
  "pof.com",
] as const;

export const WEBSITE_CATEGORIES: readonly WebsiteCategory[] = [
  { id: "social", domains: SOCIAL_DOMAINS },
  { id: "meta", domains: META_DOMAINS },
  { id: "messaging", domains: MESSAGING_DOMAINS },
  { id: "news", domains: NEWS_DOMAINS },
  { id: "dating", domains: DATING_DOMAINS },
];

const CATEGORY_BY_ID = new Map(WEBSITE_CATEGORIES.map((category) => [category.id, category]));

export function isWebsiteCategoryId(value: string): value is WebsiteCategoryId {
  return CATEGORY_BY_ID.has(value as WebsiteCategoryId);
}

export function getWebsiteCategory(id: WebsiteCategoryId): WebsiteCategory {
  return CATEGORY_BY_ID.get(id)!;
}

export function sanitizeDomainCategories(categories: string[] | undefined): WebsiteCategoryId[] {
  if (!categories?.length) return [];
  const seen = new Set<WebsiteCategoryId>();
  const result: WebsiteCategoryId[] = [];
  for (const raw of categories) {
    if (!isWebsiteCategoryId(raw) || seen.has(raw)) continue;
    seen.add(raw);
    result.push(raw);
  }
  return result;
}

export function domainsForCategories(categories: WebsiteCategoryId[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const id of categories) {
    for (const domain of getWebsiteCategory(id).domains) {
      const normalized = normalizeDomainTarget(domain);
      if (!normalized || seen.has(normalized)) continue;
      seen.add(normalized);
      result.push(normalized);
    }
  }
  return result;
}

/** Union of manual domains and all category domains, deduped and normalized. */
export function resolveTargetDomains(
  target: Pick<TargetConfig, "domains" | "domainCategories">,
): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  const addDomain = (value: string) => {
    const normalized = normalizeDomainTarget(value);
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    result.push(normalized);
  };

  for (const domain of target.domains) addDomain(domain);
  for (const domain of domainsForCategories(sanitizeDomainCategories(target.domainCategories))) {
    addDomain(domain);
  }

  return result;
}

export function targetHasDomainCategories(target: Pick<TargetConfig, "domainCategories">): boolean {
  return sanitizeDomainCategories(target.domainCategories).length > 0;
}
